import { createApiClient, apiClient } from "../../api/apiClient";
import { ObservationAPIData } from "../../types/observation";
import { setObservationMetrices } from "../../store/slices/observationSlice";
import { Dispatch } from "@reduxjs/toolkit";
import { handleObservationErrorWithFriendlyMessage, OBSERVATION_API_NAMES } from "../../store/utils/observationErrorHandler";

interface FetchObservationsMetricsParams {
  selectedLocation: { id: string; name?: string; displayText?: string } | null;
  periodWeek: any;
  employees?: any; // Add employees parameter to access active employee count
  setIsLoading: (loading: boolean) => void;
  setApiError: (error: string | null) => void;
  dispatch: Dispatch;
}

export const fetchObservationsMetrics = async ({
  selectedLocation,
  periodWeek,
  employees,
  setIsLoading,
  setApiError,
  dispatch,
}: FetchObservationsMetricsParams): Promise<void> => {
  if (!selectedLocation) {
    setApiError("No location selected");
    return;
  }

  setIsLoading(true);
  setApiError(null);

  try {
    // Use either name or displayText property depending on what's available
    const locationName =
      selectedLocation.name || selectedLocation.displayText || "";
    const location = encodeURIComponent(locationName);

    // Create API client instance
    const api = createApiClient();

    // Get Period and Week from appData state
    const period = periodWeek?.data?.period || "";
    const week = periodWeek?.data?.week || "";

    const apiUrl = `/observations?location=${location}&pageSize=${200}&pageNumber=${1}&clientTimestamp=${new Date().toISOString()}`;

    // Use apiClient instead of fetch
    const response: { observations: ObservationAPIData[]; metrics?: any } = await apiClient(api, apiUrl, "GET");
    
    // Extract observations and metrics from the response
    const data: ObservationAPIData[] = response.observations || [];
    const metrics = response.metrics || {};

    /*
    - From FormattedData, take count of observations by action completed, in progress, and new
    - Dispatch this data to the Redux store
    */
    const completedCount = data.filter(
      (item) => item.Status.toLowerCase() === "completed"
    ).length;

    const inProgressCount = data.filter(
      (item) => item.Status.toLowerCase() === "in progress"
    ).length;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const totalCount = data.length;

    // Use metrics from API response if available
    const currentWeekCompletedPulseChecks = metrics.completedPulseChecks || 0;
    
    // Sum all weeks' completed observations for the period (not just current week)
    const currentPeriodCompletedNonPulseChecks = (metrics.observationsData || []).reduce(
      (total: number, weekData: any) => total + (weekData.completedObservations || 0),
      0
    );
    
    // Get active employees count from metrics
    const activeEmployeesCount = metrics.employeeCount?.[String(metrics.week)] || 0;

    // Set the metrics based on our active employees count (avoid division by zero)
    const currentWeekCompletedObservations =
      activeEmployeesCount > 0 ? currentWeekCompletedPulseChecks : 0;
    const currentPeriodCompletedObservations =
      activeEmployeesCount > 0 ? currentPeriodCompletedNonPulseChecks : 0;

    // Calculate weekly pulse checks breakdown for the progress bar
    // Filter data for current week's pulse checks only
    const currentWeekPulseChecks = data.filter(
      (item) => 
        item.Week === metrics.week && 
        item.Period === metrics.period &&
        item.New_Hire_Annual_or_Routine?.toLowerCase() === "pulse check"
    );
    
    // Deduplicate based on employee+role+shift+observationType
    // Create unique key for each combination and only count unique combinations
    const uniqueCompletedPulseChecks = new Set<string>();
    const uniqueInProgressPulseChecks = new Set<string>();
    
    currentWeekPulseChecks.forEach((item) => {
      // Create a unique key from employee name, role, shift, and observation type
      const employeeName = (item.EE_First_Name + item.EE_Last_Name).trim() || '';
      const role = item.Role_Observed || '';
      const shift = item.Shift_Number || '';
      const observationType = item.New_Hire_Annual_or_Routine || '';
      const uniqueKey = `${employeeName}|${role}|${shift}|${observationType}`.toLowerCase();
      
      if (item.Status.toLowerCase() === "completed") {
        uniqueCompletedPulseChecks.add(uniqueKey);
      } else if (item.Status.toLowerCase() === "in progress") {
        uniqueInProgressPulseChecks.add(uniqueKey);
      }
    });
    
    const weeklyCompletedPulseChecks = uniqueCompletedPulseChecks.size;
    const weeklyInProgressPulseChecks = uniqueInProgressPulseChecks.size;

    console.log("Total Completed Count:", completedCount);
    console.log("Total In Progress Count:", inProgressCount);
    console.log("Active Employees Count:", activeEmployeesCount);
    console.log(
      `Weekly Pulse Checks (from metrics):`,
      currentWeekCompletedPulseChecks
    );
    console.log(
      `Period Observations (from metrics):`,
      currentPeriodCompletedNonPulseChecks
    );
    console.log(
      "Current Period Completed Observations:",
      currentPeriodCompletedObservations
    );
    console.log(
      "Current Week Completed Observations:",
      currentWeekCompletedObservations
    );
    console.log(
      "Weekly Completed Pulse Checks (calculated):",
      weeklyCompletedPulseChecks
    );
    console.log(
      "Weekly In Progress Pulse Checks (calculated):",
      weeklyInProgressPulseChecks
    );

    // Dispatch to Observation slice's setObservationsMetrics action
    dispatch(
      setObservationMetrices({
        completed: completedCount,
        inProgress: inProgressCount,
        total: activeEmployeesCount, // Use active employees count instead of total observations
        currentPeriodCompletedObservations: currentPeriodCompletedObservations,
        currentWeekCompletedObservations: currentWeekCompletedObservations,
        weeklyCompletedPulseChecks: weeklyCompletedPulseChecks,
        weeklyInProgressPulseChecks: weeklyInProgressPulseChecks,
      })
    );
  } catch (err) {
    console.error("Error fetching observations:", err);
    
    // Use centralized error handler with overlay
    handleObservationErrorWithFriendlyMessage(
      OBSERVATION_API_NAMES.FETCH_OBSERVATIONS,
      err,
      true, // Show error modal overlay
      {
        location: selectedLocation?.name || selectedLocation?.displayText,
        period: periodWeek?.data?.period,
        week: periodWeek?.data?.week,
      }
    );
    
    setApiError(
      err instanceof Error ? err.message : "Failed to fetch observations data"
    );
  } finally {
    setIsLoading(false);
  }
};
